import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

interface TranslatedField {
  field_name: string;
  translated_text: string;
}

/**
 * Hook to fetch translated content for a single entity from the content_translations table.
 * Returns English (original) content when the current language is English.
 * Falls back to English (fallback values) if no translation exists.
 *
 * @param entityType - Type of entity: 'scenario', 'scenario_phase', 'procedure', 'category'
 * @param entityId - UUID or string key of the entity
 * @param fields - Array of field names to translate (e.g., ['name', 'description'])
 * @param fallbacks - Record of field name -> English value (used when no translation exists)
 */
export function useTranslatedContent(
  entityType: string,
  entityId: string | undefined,
  fields: string[],
  fallbacks: Record<string, string | null | undefined>
) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || 'en';

  const { data: translations } = useQuery({
    queryKey: ['content-translation', entityType, entityId, lang],
    queryFn: async () => {
      if (!entityId || lang === 'en') return null;

      const { data } = await supabase
        .from('content_translations')
        .select('field_name, translated_text')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .eq('language_code', lang)
        .in('field_name', fields);

      return data as TranslatedField[] | null;
    },
    enabled: !!entityId && lang !== 'en',
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  // Build result map: translated value if available, otherwise fallback (English)
  const result: Record<string, string> = {};
  for (const field of fields) {
    const translated = translations?.find(t => t.field_name === field);
    result[field] = translated?.translated_text || fallbacks[field] || '';
  }

  return result;
}

interface BatchTranslation {
  entity_id: string;
  field_name: string;
  translated_text: string;
}

/**
 * Hook to fetch translated content for multiple entities at once.
 * Used for lists (e.g., scenario cards on the browse page).
 * Returns a Map of entityId -> { fieldName: translatedText }.
 *
 * @param entityType - Type of entity: 'scenario', 'scenario_phase', 'procedure', 'category'
 * @param entityIds - Array of entity IDs to fetch translations for
 * @param fields - Array of field names to translate
 */
export function useTranslatedContentBatch(
  entityType: string,
  entityIds: string[],
  fields: string[]
) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || 'en';

  // Create a stable key from sorted entity IDs (max 50 for key stability)
  const stableKey = entityIds.length <= 50
    ? entityIds.slice().sort().join(',')
    : `${entityIds.length}-${entityIds.slice(0, 5).sort().join(',')}`;

  const { data: translationsMap, isLoading } = useQuery({
    queryKey: ['content-translations-batch', entityType, stableKey, lang],
    queryFn: async () => {
      if (lang === 'en' || entityIds.length === 0) return new Map<string, Record<string, string>>();

      // Batch in groups of 100 to avoid URL length limits
      const allData: BatchTranslation[] = [];
      const batchSize = 100;

      for (let i = 0; i < entityIds.length; i += batchSize) {
        const batch = entityIds.slice(i, i + batchSize);
        const { data } = await supabase
          .from('content_translations')
          .select('entity_id, field_name, translated_text')
          .eq('entity_type', entityType)
          .eq('language_code', lang)
          .in('entity_id', batch)
          .in('field_name', fields);

        if (data) allData.push(...(data as BatchTranslation[]));
      }

      // Build nested map: entityId -> fieldName -> translatedText
      const map = new Map<string, Record<string, string>>();
      for (const row of allData) {
        if (!map.has(row.entity_id)) map.set(row.entity_id, {});
        map.get(row.entity_id)![row.field_name] = row.translated_text;
      }
      return map;
    },
    enabled: lang !== 'en' && entityIds.length > 0,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  /**
   * Get a translated field value for a specific entity.
   * Falls back to the provided English value if no translation exists.
   */
  const getTranslation = (entityId: string, fieldName: string, fallback: string): string => {
    if (lang === 'en') return fallback;
    const entityTranslations = translationsMap?.get(entityId);
    return entityTranslations?.[fieldName] || fallback;
  };

  return {
    translationsMap: translationsMap || new Map<string, Record<string, string>>(),
    getTranslation,
    isLoading,
    isEnglish: lang === 'en',
  };
}
